import React, { forwardRef, useId } from 'react';
import './field.css';

/*
 * Field system.
 *
 *   <Field label="Emirates ID" required help="15 digits" error={errors.eid}>
 *     <Input value={eid} onChange={...} />
 *   </Field>
 *
 * Field links the label, help and error text to its control (id, aria-describedby,
 * aria-invalid, required), so every form gets proper labels without extra work.
 */

const joinIds = (...ids) => ids.filter(Boolean).join(' ') || undefined;

export function Field({
    label,
    htmlFor,
    id,
    required = false,
    optional = false,
    help,
    error,
    children,
    className = '',
    labelAction,
}) {
    const auto = useId();
    const child = React.isValidElement(children) ? children : null;
    const fieldId = htmlFor || id || (child && child.props.id) || `fld-${auto}`;
    const helpId = help ? `${fieldId}-help` : undefined;
    const errorId = error ? `${fieldId}-error` : undefined;

    const control = child
        ? React.cloneElement(child, {
            id: child.props.id || fieldId,
            required: child.props.required ?? (required || undefined),
            'aria-invalid': error ? true : child.props['aria-invalid'],
            'aria-describedby': joinIds(child.props['aria-describedby'], errorId, helpId),
        })
        : children;

    return (
        <div className={`ds-field ${error ? 'has-error' : ''} ${className}`}>
            {label && (
                <div className="ds-field__label-row">
                    <label className="ds-field__label" htmlFor={fieldId}>
                        {label}
                        {required && <span className="ds-field__required" aria-hidden="true">*</span>}
                        {optional && <span className="ds-field__optional">optional</span>}
                    </label>
                    {labelAction}
                </div>
            )}
            {control}
            {error ? (
                <div id={errorId} className="ds-field__error" role="alert">{error}</div>
            ) : help ? (
                <div id={helpId} className="ds-field__help">{help}</div>
            ) : null}
        </div>
    );
}

export const Input = forwardRef(function Input(
    { size = 'md', prefix, suffix, invalid, className = '', type = 'text', ...rest },
    ref
) {
    const input = (
        <input
            ref={ref}
            type={type}
            className={`ds-input ds-input--${size} ${invalid ? 'is-invalid' : ''} ${prefix || suffix ? '' : className}`}
            aria-invalid={invalid ? true : rest['aria-invalid']}
            {...rest}
        />
    );
    if (!prefix && !suffix) return input;
    return (
        <div className={`ds-input-group ds-input-group--${size} ${invalid ? 'is-invalid' : ''} ${className}`}>
            {prefix && <span className="ds-input-group__addon">{prefix}</span>}
            {input}
            {suffix && <span className="ds-input-group__addon">{suffix}</span>}
        </div>
    );
});

export const Select = forwardRef(function Select(
    { size = 'md', invalid, className = '', children, options, placeholder, ...rest },
    ref
) {
    return (
        <select
            ref={ref}
            className={`ds-input ds-select ds-input--${size} ${invalid ? 'is-invalid' : ''} ${className}`}
            aria-invalid={invalid ? true : rest['aria-invalid']}
            {...rest}
        >
            {placeholder !== undefined && <option value="">{placeholder}</option>}
            {options
                ? options.map((o) => {
                    const opt = typeof o === 'object' ? o : { value: o, label: o };
                    return <option key={String(opt.value)} value={opt.value} disabled={opt.disabled}>{opt.label}</option>;
                })
                : children}
        </select>
    );
});

export const Textarea = forwardRef(function Textarea({ invalid, className = '', rows = 3, ...rest }, ref) {
    return (
        <textarea
            ref={ref}
            rows={rows}
            className={`ds-input ds-textarea ${invalid ? 'is-invalid' : ''} ${className}`}
            aria-invalid={invalid ? true : rest['aria-invalid']}
            {...rest}
        />
    );
});

export const Checkbox = forwardRef(function Checkbox({ label, description, className = '', id, ...rest }, ref) {
    const auto = useId();
    const cid = id || `chk-${auto}`;
    return (
        <label className={`ds-check ${className}`} htmlFor={cid}>
            <input ref={ref} id={cid} type="checkbox" className="ds-check__input" {...rest} />
            {(label || description) && (
                <span className="ds-check__text">
                    {label && <span className="ds-check__label">{label}</span>}
                    {description && <span className="ds-check__desc">{description}</span>}
                </span>
            )}
        </label>
    );
});

/** Switch: an on/off toggle (role="switch"). */
export function Switch({ checked, onChange, label, description, disabled, id, className = '' }) {
    const auto = useId();
    const sid = id || `sw-${auto}`;
    return (
        <div className={`ds-switch-row ${className}`}>
            {(label || description) && (
                <label className="ds-check__text" htmlFor={sid}>
                    {label && <span className="ds-check__label">{label}</span>}
                    {description && <span className="ds-check__desc">{description}</span>}
                </label>
            )}
            <button
                id={sid}
                type="button"
                role="switch"
                aria-checked={Boolean(checked)}
                disabled={disabled}
                className={`ds-switch ${checked ? 'is-on' : ''}`}
                onClick={() => onChange && onChange(!checked)}
            >
                <span className="ds-switch__thumb" />
            </button>
        </div>
    );
}

/** RadioCards: a short list of choices shown as selectable cards (a radio group). */
export function RadioCards({ name, value, onChange, options = [], columns, label, className = '' }) {
    const auto = useId();
    const groupName = name || `rc-${auto}`;
    return (
        <div
            role="radiogroup"
            aria-label={label}
            className={`ds-radio-cards ${className}`}
            style={columns ? { gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` } : undefined}
        >
            {options.map((o) => {
                const opt = typeof o === 'object' ? o : { value: o, label: o };
                const checked = String(value) === String(opt.value);
                return (
                    <label key={String(opt.value)} className={`ds-radio-card ${checked ? 'is-checked' : ''}`}>
                        <input
                            type="radio"
                            name={groupName}
                            value={opt.value}
                            checked={checked}
                            disabled={opt.disabled}
                            onChange={() => onChange && onChange(opt.value)}
                        />
                        <span>
                            <span className="ds-radio-card__label">{opt.label}</span>
                            {opt.description && <span className="ds-radio-card__desc">{opt.description}</span>}
                        </span>
                    </label>
                );
            })}
        </div>
    );
}

/** FormSection: title + description on the left, fields on the right (stacks on small screens). */
export function FormSection({ title, description, children, className = '' }) {
    return (
        <section className={`ds-form-section ${className}`}>
            {(title || description) && (
                <div className="ds-form-section__intro">
                    {title && <h3 className="ds-form-section__title">{title}</h3>}
                    {description && <p className="ds-form-section__desc">{description}</p>}
                </div>
            )}
            <div className="ds-form-section__body">{children}</div>
        </section>
    );
}

/** FormGrid: responsive columns for fields. */
export function FormGrid({ cols = 2, children, className = '' }) {
    return <div className={`ds-form-grid ds-form-grid--${cols} ${className}`}>{children}</div>;
}

/**
 * FormFooter: the action bar at the end of a form. Sticky by default so the primary action
 * stays reachable on long forms. `status` shows on the left (e.g. "Draft saved 12 s ago").
 */
export function FormFooter({ children, status, sticky = true, className = '' }) {
    return (
        <div className={`ds-form-footer ${sticky ? 'is-sticky' : ''} ${className}`}>
            <div className="ds-form-footer__status" aria-live="polite">{status}</div>
            <div className="ds-form-footer__actions">{children}</div>
        </div>
    );
}

export default Field;
